import {
    ReactElement,
    useId,
    ReactNode,
    MouseEvent,
    useRef,
    useEffect
} from 'react';
import styles from './ui-checkbox.module.scss';
import { UIText } from '../ui-text';

export type UICheckboxProps = {
    name?: string;
    checked?: boolean;
    /** Shows a "partial" state (e.g. select-all when only some are selected). */
    indeterminate?: boolean;
    description?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    small?: boolean;
    errorText?: string;
    onChange?: (check: boolean) => void;
    onClick?: (event: MouseEvent<HTMLInputElement>) => void;
};
export function UICheckbox(props: UICheckboxProps): ReactElement {
    const {
        name,
        checked,
        indeterminate = false,
        description = '',
        required = false,
        disabled = false,
        small = false,
        errorText = '',
        onChange,
        onClick
    } = props;

    const id = useId();

    // `indeterminate` is only settable via the DOM, not as an attribute.
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const checkboxControlStyle = [styles.checkboxControl];
    if (small) {
        checkboxControlStyle.push(styles.checkboxControlSmallSize);
    } else {
        checkboxControlStyle.push(styles.checkboxControlNormalSize);
    }
    return (
        <div className={styles.container}>
            <label
                className={styles.customCheckbox}
                htmlFor={id}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <input
                    ref={inputRef}
                    type="checkbox"
                    id={id}
                    checked={checked}
                    name={name}
                    required={required}
                    disabled={disabled}
                    onClick={onClick}
                    onChange={(e) => {
                        onChange?.(e.target.checked);
                    }}
                />
                <div
                    style={{
                        minWidth: small ? 16 : 20,
                        minHeight: small ? 16 : 20
                    }}
                    className={checkboxControlStyle.join(' ')}
                />
                {description && (
                    <div style={{ marginLeft: 16 }}>
                        <UIText>
                            {description}
                            {required && (
                                <span className={styles.descriptionRequired}>
                                    {' *'}
                                </span>
                            )}
                        </UIText>
                    </div>
                )}
            </label>
            {errorText && <p className={styles.errorText}>{errorText}</p>}
        </div>
    );
}
